import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex, Text, Avatar, Button } from "@chakra-ui/react";
import { Check, Clock, XClose, Calendar, VideoRecorder } from "~/icons";
import type { Consultation } from "sdk/consultations";

interface Props {
  consultation: Consultation;
}

export const ConsultationCard: FC<Props> = ({ consultation }) => {
  const { t } = useTranslation();

  const formatDate = (consultation: Consultation) => {
    if (!consultation.scheduled_datetime) return "";

    const date = new Date(consultation.scheduled_datetime);
    const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${dayName} • ${formattedDate}`;
  };

  const StatusLabel = () => {
    const statusConfig = {
      pending: {
        bg: "th.yellow1",
        color: "th.black",
        borderColor: "th.black",
        text: "Pending",
        icon: "clock",
      },
      assigned: {
        bg: "th.yellowGreen1",
        color: "th.signatureGreen",
        borderColor: "th.signatureGreen",
        text: "Consultation scheduled",
        icon: "check",
      },
      scheduled: {
        bg: "th.yellowGreen1",
        color: "th.signatureGreen",
        borderColor: "th.signatureGreen",
        text: "Consultation scheduled",
        icon: "check",
      },
      completed: {
        bg: "th.yellowGreen1",
        color: "th.signatureGreen",
        borderColor: "th.signatureGreen",
        text: "Consultation completed",
        icon: "check",
      },
      rejected: {
        bg: "red.50",
        color: "red.600",
        borderColor: "red.600",
        text: "Consultation rejected",
        icon: "x",
      },
      denied: {
        bg: "red.50",
        color: "red.600",
        borderColor: "red.600",
        text: "Consultation denied",
        icon: "x",
      },
      canceled: {
        bg: "gray.100",
        color: "gray.600",
        borderColor: "gray.600",
        text: "Consultation canceled",
        icon: "x",
      },
      no_show: {
        bg: "th.yellow1",
        color: "th.black",
        borderColor: "th.black",
        text: "Missed consultation",
        icon: "clock",
      },
    };

    const config =
      consultation.status &&
      statusConfig[consultation.status as keyof typeof statusConfig];

    if (!config) return null;

    return (
      <Box position="relative" display="inline-flex">
        <Flex
          align="center"
          gap={2}
          bg={config.bg}
          px={6}
          py="5px"
          position="relative"
          sx={{
            clipPath: "polygon(16px 0, 100% 0, calc(100% - 16px) 100%, 0 100%)",
          }}
        >
          {config.icon === "check" ? (
            <Flex
              align="center"
              justify="center"
              boxSize="20px"
              borderRadius="full"
              border="1.75px solid"
              borderColor={config.borderColor}
            >
              <Check
                width="10px"
                height="10px"
                color={
                  config.color === "th.signatureGreen"
                    ? "#1A5544"
                    : "currentColor"
                }
                strokeWidth="4"
              />
            </Flex>
          ) : config.icon === "x" ? (
            <Flex
              align="center"
              justify="center"
              boxSize="20px"
              borderRadius="full"
              border="1.75px solid"
              borderColor={config.borderColor}
            >
              <XClose
                width="10px"
                height="10px"
                color={config.color}
                strokeWidth="4"
              />
            </Flex>
          ) : (
            <Clock
              width="20px"
              height="20px"
              color={config.color === "th.black" ? "#111A18" : "currentColor"}
              strokeWidth="2"
            />
          )}
          <Text
            fontSize="16px"
            fontWeight="bold"
            color={config.color}
            lineHeight="1.4"
          >
            {config.text}
          </Text>
        </Flex>
      </Box>
    );
  };

  const FoundationButton = () => {
    const excludedStatuses = ["pending", "rejected", "denied"];

    if (
      false ||
      consultation.external_url &&
      consultation.status &&
      !excludedStatuses.includes(consultation.status)
    ) {
      return (
        <Button
          as="a"
          href={consultation.external_url}
          target="_blank"
          rel="noopener noreferrer"
          bg="th.signatureGreen"
          color="white"
          fontSize="14px"
          fontWeight="bold"
          lineHeight="1.4"
          px={6}
          py={4}
          height="auto"
          borderRadius="xl"
          _hover={{ bg: "newDarkGreen.700" }}
        >
          {(t as any)(
            `diagnosticDashboard.consultation.buttons.${consultation.status}`,
          )}
        </Button>
      );
    }

    return null;
  };

  const AcuityButton = () => {
    return (
      <Button
        as="a"
        href={consultation.external_url}
        target="_blank"
        rel="noopener noreferrer"
        bg="th.signatureGreen"
        color="white"
        fontSize="14px"
        fontWeight="bold"
        lineHeight="1.4"
        px={6}
        py={4}
        height="auto"
        borderRadius="xl"
        _hover={{ bg: "newDarkGreen.700" }}
      >
        {isAppointmentInFuture()
          ? t("diagnosticDashboard.consultation.buttons.editConsultation")
          : t("diagnosticDashboard.consultation.buttons.viewConsultation")}
      </Button>
    );
  };

  const renderPrimaryButton = () => {
    if (consultation.acuity_calendar_id) {
      return <AcuityButton />;
    } else {
      return <FoundationButton />;
    }
  };

  const formatTime = (consultation: Consultation) => {
    if (!consultation.scheduled_datetime) return "";

    const startDate = new Date(consultation.scheduled_datetime);
    const startTime = startDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    // If duration is available, calculate and display end time
    if (consultation.duration) {
      const endDate = new Date(startDate.getTime() + consultation.duration * 60000);
      const endTime = endDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      return `${startTime}–${endTime}`;
    }

    return startTime;
  };

  const isAppointmentInFuture = () => {
    if (!consultation.scheduled_datetime) return false;

    const appointmentDate = new Date(consultation.scheduled_datetime);
    const now = new Date();
    return appointmentDate > now;
  };

  return (
    <Box
      bg="white"
      borderRadius="lg"
      boxShadow="0px 5px 20px 0px rgba(0, 0, 0, 0.05)"
      p={4}
      border="1px solid"
      borderColor="#E7E8E8"
      position="relative"
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align="start"
        gap={4}
      >
        <Flex direction="column" gap={4}>
          <Flex align="start" gap="14px">
            <Avatar
              size="md"
              bg="#86B0BE"
              name={consultation.provider_name}
              w="45px"
              h="45px"
            />
            <Flex direction="column" gap={0}>
              <Text
                fontSize="18px"
                fontWeight="bold"
                color="#111A18"
                lineHeight="1.4"
              >
                {consultation.consultation_type}
              </Text>
              {consultation.provider_name && (
                <Text
                  fontSize="18px"
                  fontWeight="normal"
                  color="#111A18"
                  lineHeight="1.4"
                >
                  {consultation.provider_name}
                </Text>
              )}
            </Flex>
          </Flex>

          <Flex direction="column" gap="7px">
            {consultation.scheduled_datetime && (
              <Flex align="center" gap="10px">
                <Calendar
                  width="18px"
                  height="18px"
                  color="#111A18"
                  strokeWidth="1.5"
                />
                <Text
                  fontSize="16px"
                  fontWeight="normal"
                  color="#111A18"
                  lineHeight="1.4"
                >
                  {formatDate(consultation)}
                </Text>
              </Flex>
            )}
            {consultation.scheduled_datetime && (
              <Flex align="center" gap="10px">
                <Clock
                  width="18px"
                  height="18px"
                  color="#111A18"
                  strokeWidth="1.5"
                />
                <Text
                  fontSize="16px"
                  fontWeight="normal"
                  color="#111A18"
                  lineHeight="1.4"
                >
                  {formatTime(consultation)}
                </Text>
              </Flex>
            )}
            {consultation.scheduled_datetime && (
              <Flex align="center" gap="10px">
                <VideoRecorder
                  width="13.5px"
                  height="13.5px"
                  color="#111A18"
                  strokeWidth="1.5"
                />
                <Text
                  fontSize="16px"
                  fontWeight="normal"
                  color="#111A18"
                  lineHeight="1.4"
                >
                  {t("diagnosticDashboard.consultation.videoConferencing")}
                </Text>
              </Flex>
            )}
            {consultation.source == "foundation_health" && (
              <Flex align="center" gap="10px">
                <Text
                  fontSize="16px"
                  fontWeight="normal"
                  color="#111A18"
                  lineHeight="1.4"
                >
                  {t("diagnosticDashboard.consultation.asyncVisit")}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>

        <Flex
          justify={{ base: "stretch", md: "flex-end" }}
          direction="column"
          gap={8}
          align="flex-end"
        >
          <StatusLabel />
          {renderPrimaryButton()}
        </Flex>
      </Flex>
    </Box>
  );
};
